import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBell,
  FaUserPlus,
  FaEdit,
  FaBan,
  FaTrashAlt,
  FaUsers,
  FaUserShield,
  FaMapMarkedAlt,
  FaSitemap,
  FaClipboardList,
  FaExclamationTriangle,
  FaTasks,
  FaCheckCircle,
} from 'react-icons/fa';
import notificationService from '../../services/notificationService';
import { notificationAPI } from '../../utils/api';
import './NotificationBell.css';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Load notifications from API
    loadNotifications();

    // Subscribe to notification service events
    const handleNotificationAdded = (notification) => {
      setNotifications(prev => [notification, ...prev]);
    };

    const handleUnreadCountChanged = (count) => {
      setUnreadCount(count);
    };

    const handleNotificationsLoaded = (loadedNotifications) => {
      setNotifications(prev => {
        if (prev.length > 0) {
          const backendIds = new Set(loadedNotifications.map(n => n.backendId));
          const existingNotifications = prev.filter(n => !backendIds.has(n.backendId));
          return [...existingNotifications, ...loadedNotifications];
        }
        return loadedNotifications;
      });
    };

    notificationService.on('notification-added', handleNotificationAdded);
    notificationService.on('unread-count-changed', handleUnreadCountChanged);
    notificationService.on('notifications-loaded', handleNotificationsLoaded);

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      notificationService.off('notification-added', handleNotificationAdded);
      notificationService.off('unread-count-changed', handleUnreadCountChanged);
      notificationService.off('notifications-loaded', handleNotificationsLoaded);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.getAll({ limit: 50 });
      
      let notificationsData = [];
      if (response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          notificationsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          notificationsData = response.data;
        } else if (response.data.notifications && Array.isArray(response.data.notifications)) {
          notificationsData = response.data.notifications;
        }
      }
      
      notificationService.setNotifications(notificationsData);
    } catch (error) {
      const localNotifications = notificationService.getNotifications();
      setNotifications(localNotifications);
      setUnreadCount(notificationService.getUnreadCount());
    } finally {
      setLoading(false);
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const getNotificationRoute = (type, message = '') => {
    const msg = (message || '').toLowerCase();

    const typeRoutes = {
      'personnel-added':    '/admin/personnel',
      'personnel-updated':  '/admin/personnel',
      'personnel-disabled': '/admin/personnel',
      'personnel-enabled':  '/admin/personnel',
      'personnel-deleted':  '/admin/personnel',
      'personnel':          '/admin/personnel',
      'sub-admin':          '/admin/subadmins',
      'admin':              '/admin/subadmins',
      'admin-added':        '/admin/subadmins',
      'admin-updated':      '/admin/subadmins',
      'admin-deleted':      '/admin/subadmins',
      'site':               '/admin/sites',
      'site-added':         '/admin/sites',
      'site-updated':       '/admin/sites',
      'site-deleted':       '/admin/sites',
      'site_added':         '/admin/sites',
      'site_updated':       '/admin/sites',
      'site_deleted':       '/admin/sites',
      'structure':          '/admin/structure',
      'branch-added':       '/admin/structure',
      'region-added':       '/admin/structure',
      'site-assigned':      '/admin/structure',
      'site_assigned':      '/admin/structure',
      'coverage':           '/admin/coverage',
      'coverage-submitted': '/admin/coverage',
      'coverage-edited':    '/admin/coverage',
      'coverage_submitted': '/admin/coverage',
      'coverage_edited':    '/admin/coverage',
      'client':             '/admin/clients',
      'client-added':       '/admin/clients',
      'client_added':       '/admin/clients',
      'incident':           '/admin/incident-reports',
      'incident-submitted': '/admin/incident-reports',
      'incident-status-updated': '/admin/incident-reports',
      'incident_submitted': '/admin/incident-reports',
      'incident_status_updated': '/admin/incident-reports',
      'assignment':         '/admin/special-assignments',
      'special-assignment': '/admin/special-assignments',
      'assignment-submitted': '/admin/special-assignments',
      'assignment-assigned': '/admin/special-assignments',
      'assignment-deleted': '/admin/special-assignments',
      'assignment-approved': '/admin/special-assignments',
      'assignment-accepted': '/admin/special-assignments',
      'assignment-rejected': '/admin/special-assignments',
      'assignment-completed': '/admin/special-assignments',
      'assignment_submitted': '/admin/special-assignments',
      'assignment_assigned': '/admin/special-assignments',
      'assignment_deleted': '/admin/special-assignments',
      'assignment_approved': '/admin/special-assignments',
      'assignment_accepted': '/admin/special-assignments',
      'assignment_rejected': '/admin/special-assignments',
      'assignment_completed': '/admin/special-assignments',
    };

    if (type && typeRoutes[type]) return typeRoutes[type];

    // Check more specific keywords first before generic ones
    // IMPORTANT: Check for 'coverage', 'client', 'incident', and 'assignment' BEFORE 'personnel' because those messages often contain 'personnel' keyword
    if (msg.includes('coverage'))         return '/admin/coverage';
    if (msg.includes('client'))           return '/admin/clients';
    if (msg.includes('incident') || msg.includes('report')) return '/admin/incident-reports';
    if (msg.includes('assignment'))       return '/admin/special-assignments';
    if (msg.includes('personnel'))        return '/admin/personnel';
    if (msg.includes('site') && !msg.includes('assigned'))  return '/admin/sites';
    if (msg.includes('branch') || msg.includes('region') || msg.includes('structure'))
                                          return '/admin/structure';
    if (msg.includes('sub-admin') || (msg.includes('admin') && !msg.includes('dashboard')))
                                          return '/admin/subadmins';
    if (msg.includes('statistic'))        return '/admin/statistics';

    return null;
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const notification = notifications.find(n => n.id === notificationId);
      const backendId = notification?.backendId || notificationId;
      
      await notificationAPI.markAsRead(backendId);
      notificationService.markAsRead(notificationId);
    } catch (error) {
      notificationService.markAsRead(notificationId);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }
    const route = getNotificationRoute(notification.type, notification.message);
    if (route) {
      setIsOpen(false);
      navigate(route);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      notificationService.markAllAsRead();
    } catch (error) {
      notificationService.markAllAsRead();
    }
  };

  const handleClearAll = async () => {
    try {
      await Promise.all(notifications.map(n => notificationAPI.delete(n.backendId || n.id)));
    } catch (error) {
      // best-effort
    } finally {
      setNotifications([]);
      setUnreadCount(0);
      notificationService.clear();
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type, message = '') => {
    const s = { fontSize: '20px', flexShrink: 0 };
    const msg = (message || '').toLowerCase();

    // --- type-based (exact backend enum values) ---
    const typeMap = {
      'personnel-added':   <FaUserPlus style={{ ...s, color: '#28a745' }} title="Personnel Added" />,
      'personnel-updated': <FaEdit style={{ ...s, color: '#007bff' }} title="Personnel Updated" />,
      'personnel-disabled':<FaBan style={{ ...s, color: '#ffc107' }} title="Personnel Status Changed" />,
      'personnel-enabled': <FaCheckCircle style={{ ...s, color: '#28a745' }} title="Personnel Enabled" />,
      'personnel-deleted': <FaTrashAlt style={{ ...s, color: '#dc3545' }} title="Personnel Deleted" />,
      'personnel':         <FaUsers style={{ ...s, color: '#007bff' }} title="Personnel Management" />,
      'sub-admin':         <FaUserShield style={{ ...s, color: '#6f42c1' }} title="Admin Management" />,
      'admin':             <FaUserShield style={{ ...s, color: '#6f42c1' }} title="Admin Management" />,
      'admin-added':       <FaUserShield style={{ ...s, color: '#28a745' }} title="Admin Added" />,
      'admin-updated':     <FaUserShield style={{ ...s, color: '#007bff' }} title="Admin Updated" />,
      'admin-deleted':     <FaUserShield style={{ ...s, color: '#dc3545' }} title="Admin Deleted" />,
      'site':              <FaMapMarkedAlt style={{ ...s, color: '#17a2b8' }} title="Site Management" />,
      'site-added':        <FaMapMarkedAlt style={{ ...s, color: '#28a745' }} title="Site Added" />,
      'site-updated':      <FaMapMarkedAlt style={{ ...s, color: '#007bff' }} title="Site Updated" />,
      'site-deleted':      <FaMapMarkedAlt style={{ ...s, color: '#dc3545' }} title="Site Deleted" />,
      'site_added':        <FaMapMarkedAlt style={{ ...s, color: '#28a745' }} title="Site Added" />,
      'site_updated':      <FaMapMarkedAlt style={{ ...s, color: '#007bff' }} title="Site Updated" />,
      'site_deleted':      <FaMapMarkedAlt style={{ ...s, color: '#dc3545' }} title="Site Deleted" />,
      'structure':         <FaSitemap style={{ ...s, color: '#fd7e14' }} title="Structure Management" />,
      'branch-added':      <FaSitemap style={{ ...s, color: '#28a745' }} title="Branch Added" />,
      'region-added':      <FaSitemap style={{ ...s, color: '#28a745' }} title="Region Added" />,
      'site-assigned':     <FaSitemap style={{ ...s, color: '#007bff' }} title="Sites Assigned" />,
      'site_assigned':     <FaSitemap style={{ ...s, color: '#007bff' }} title="Sites Assigned" />,
      'coverage':          <FaClipboardList style={{ ...s, color: '#20c997' }} title="Coverage" />,
      'coverage-submitted':<FaClipboardList style={{ ...s, color: '#28a745' }} title="Coverage Submitted" />,
      'coverage-edited':   <FaClipboardList style={{ ...s, color: '#007bff' }} title="Coverage Edited" />,
      'coverage_submitted':<FaClipboardList style={{ ...s, color: '#28a745' }} title="Coverage Submitted" />,
      'coverage_edited':   <FaClipboardList style={{ ...s, color: '#007bff' }} title="Coverage Edited" />,
      'client':            <FaUserPlus style={{ ...s, color: '#17a2b8' }} title="New Client" />,
      'client-added':      <FaUserPlus style={{ ...s, color: '#28a745' }} title="Client Added" />,
      'client_added':      <FaUserPlus style={{ ...s, color: '#28a745' }} title="Client Added" />,
      'incident':          <FaExclamationTriangle style={{ ...s, color: '#dc3545' }} title="Incident Report" />,
      'incident-submitted': <FaExclamationTriangle style={{ ...s, color: '#ffc107' }} title="Incident Submitted" />,
      'incident-status-updated': <FaExclamationTriangle style={{ ...s, color: '#007bff' }} title="Incident Status Updated" />,
      'incident_submitted': <FaExclamationTriangle style={{ ...s, color: '#ffc107' }} title="Incident Submitted" />,
      'incident_status_updated': <FaExclamationTriangle style={{ ...s, color: '#007bff' }} title="Incident Status Updated" />,
      'assignment':        <FaTasks style={{ ...s, color: '#17a2b8' }} title="Special Assignment" />,
      'special-assignment':<FaTasks style={{ ...s, color: '#17a2b8' }} title="Special Assignment" />,
      'assignment-submitted': <FaTasks style={{ ...s, color: '#28a745' }} title="Assignment Submitted" />,
      'assignment-assigned': <FaTasks style={{ ...s, color: '#007bff' }} title="Assignment Assigned" />,
      'assignment-deleted': <FaTasks style={{ ...s, color: '#dc3545' }} title="Assignment Deleted" />,
      'assignment-approved': <FaTasks style={{ ...s, color: '#28a745' }} title="Assignment Approved" />,
      'assignment-accepted': <FaTasks style={{ ...s, color: '#28a745' }} title="Assignment Accepted" />,
      'assignment-rejected': <FaTasks style={{ ...s, color: '#dc3545' }} title="Assignment Rejected" />,
      'assignment-completed': <FaTasks style={{ ...s, color: '#007bff' }} title="Assignment Completed" />,
      'assignment_submitted': <FaTasks style={{ ...s, color: '#28a745' }} title="Assignment Submitted" />,
      'assignment_assigned': <FaTasks style={{ ...s, color: '#007bff' }} title="Assignment Assigned" />,
      'assignment_deleted': <FaTasks style={{ ...s, color: '#dc3545' }} title="Assignment Deleted" />,
      'assignment_approved': <FaTasks style={{ ...s, color: '#28a745' }} title="Assignment Approved" />,
      'assignment_accepted': <FaTasks style={{ ...s, color: '#28a745' }} title="Assignment Accepted" />,
      'assignment_rejected': <FaTasks style={{ ...s, color: '#dc3545' }} title="Assignment Rejected" />,
      'assignment_completed': <FaTasks style={{ ...s, color: '#007bff' }} title="Assignment Completed" />,
      'success':           <FaCheckCircle style={{ ...s, color: '#28a745' }} title="Success" />,
    };

    if (type && typeMap[type]) return typeMap[type];

    // --- message keyword fallback (handles generic/null types from backend) ---
    // Check more specific keywords first to avoid false matches
    // IMPORTANT: Check for 'coverage', 'client', 'incident', and 'assignment' FIRST because those messages often contain 'personnel' keyword
    if (msg.includes('coverage'))
      return <FaClipboardList style={{ ...s, color: '#20c997' }} title="Coverage" />;
    
    // Check for 'client' before 'personnel' since client messages say "Personnel X added client Y"
    if (msg.includes('client'))
      return <FaUserPlus style={{ ...s, color: '#17a2b8' }} title="New Clients" />;
    
    // Check for 'incident' before 'personnel' since incident messages say "Personnel X submitted incident report"
    if (msg.includes('incident'))
      return <FaExclamationTriangle style={{ ...s, color: '#dc3545' }} title="Incident Report" />;
    
    // Check for 'assignment' before 'personnel' since assignment messages say "Personnel X submitted assignment"
    // Check for specific assignment actions first for accurate color coding
    if (msg.includes('assignment') && (msg.includes('deleted') || msg.includes('delet')))
      return <FaTasks style={{ ...s, color: '#dc3545' }} title="Assignment Deleted" />;
    if (msg.includes('assignment') && msg.includes('reject'))
      return <FaTasks style={{ ...s, color: '#dc3545' }} title="Assignment Rejected" />;
    if (msg.includes('assignment') && (msg.includes('approved') || msg.includes('approve')))
      return <FaTasks style={{ ...s, color: '#28a745' }} title="Assignment Approved" />;
    if (msg.includes('assignment') && (msg.includes('accepted') || msg.includes('accept')))
      return <FaTasks style={{ ...s, color: '#28a745' }} title="Assignment Accepted" />;
    if (msg.includes('assignment') && (msg.includes('submitted') || msg.includes('submit')))
      return <FaTasks style={{ ...s, color: '#28a745' }} title="Assignment Submitted" />;
    if (msg.includes('assignment') && (msg.includes('completed') || msg.includes('complete')))
      return <FaTasks style={{ ...s, color: '#007bff' }} title="Assignment Completed" />;
    if (msg.includes('assignment') && msg.includes('assign'))
      return <FaTasks style={{ ...s, color: '#007bff' }} title="Assignment Assigned" />;
    if (msg.includes('assignment'))
      return <FaTasks style={{ ...s, color: '#17a2b8' }} title="Special Assignment" />;
    
    if (msg.includes('deleted personnel') || (msg.includes('personnel') && msg.includes('delet')))
      return <FaTrashAlt style={{ ...s, color: '#dc3545' }} title="Personnel Deleted" />;
    if (msg.includes('added personnel') || (msg.includes('personnel') && msg.includes('add')))
      return <FaUserPlus style={{ ...s, color: '#28a745' }} title="Personnel Added" />;
    if (msg.includes('updated personnel') || msg.includes('modified personnel') || (msg.includes('personnel') && msg.includes('updat')))
      return <FaEdit style={{ ...s, color: '#007bff' }} title="Personnel Updated" />;
    if ((msg.includes('personnel') && (msg.includes('disabled') || msg.includes('enabled') || msg.includes('status'))))
      return <FaBan style={{ ...s, color: '#ffc107' }} title="Personnel Status Changed" />;
    if (msg.includes('personnel'))
      return <FaUsers style={{ ...s, color: '#007bff' }} title="Personnel Management" />;

    // Check for site-assigned-to-region before plain site check so structure icon is used
    if ((msg.includes('site') || msg.includes('sites')) && (msg.includes('assigned') || msg.includes('region')))
      return <FaSitemap style={{ ...s, color: '#007bff' }} title="Sites Assigned" />;

    // Check for 'site' before 'admin' to avoid confusion with "Admin X added site Y"
    if (msg.includes('deleted site') || (msg.includes('site') && msg.includes('delet')))
      return <FaMapMarkedAlt style={{ ...s, color: '#dc3545' }} title="Site Deleted" />;
    if (msg.includes('added site') || (msg.includes('site') && msg.includes('add')))
      return <FaMapMarkedAlt style={{ ...s, color: '#28a745' }} title="Site Added" />;
    if (msg.includes('updated site') || (msg.includes('site') && msg.includes('updat')))
      return <FaMapMarkedAlt style={{ ...s, color: '#007bff' }} title="Site Updated" />;
    if (msg.includes('site'))
      return <FaMapMarkedAlt style={{ ...s, color: '#17a2b8' }} title="Site Management" />;

    if (msg.includes('admin') || msg.includes('sub-admin'))
      return <FaUserShield style={{ ...s, color: '#6f42c1' }} title="Admin Management" />;
    
    // Check for structure-related keywords
    if (msg.includes('branch') || msg.includes('region') || msg.includes('structure'))
      return <FaSitemap style={{ ...s, color: '#fd7e14' }} title="Structure Management" />;

    return <FaUsers style={{ ...s, color: '#6c757d' }} title="Notification" />;
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button 
        className="notification-bell-button" 
        onClick={toggleDropdown}
        aria-label="Notifications"
      >
        <FaBell className="bell-icon" />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-btn" 
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                  onClick={() => handleNotificationClick(notification)}
                  style={{ cursor: getNotificationRoute(notification.type, notification.message) ? 'pointer' : 'default' }}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type, notification.message)}
                  </div>
                  <div className="notification-content">
                    <p className="notification-message">{notification.message}</p>
                    <span className="notification-time">
                      {formatTimestamp(notification.timestamp)}
                    </span>
                  </div>
                  {!notification.read && <div className="unread-indicator"></div>}
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <button className="view-all-btn" onClick={handleClearAll}>Clear All Notifications</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
